<template>
  <article
    v-reveal="revealDelay"
    class="group flex h-full flex-col overflow-hidden rounded-2xl bg-default p-4 shadow-sm ring-1 transition-all duration-200"
    :class="isUnavailable
      ? 'ring-default opacity-90'
      : hasDiscount
        ? 'ring-rose-200 dark:ring-rose-900 bg-gradient-to-b from-rose-50/80 to-default dark:from-rose-950/40 hover:shadow-md hover:ring-rose-300 dark:hover:ring-rose-800'
        : 'ring-default hover:shadow-md'"
  >
    <div class="relative mb-4">
      <NuxtLink
        :to="shopPath(`/produse/${product.slug}`)"
        class="block aspect-[4/5] overflow-hidden rounded-2xl bg-muted"
        @mouseenter="prefetchProduct"
        @focus="prefetchProduct"
      >
        <div
          ref="imageWrapRef"
          class="product-card-media h-full w-full"
          :class="isUnavailable ? '' : 'product-card-media--hover'"
        >
          <AppImage
            :src="cardImageUrl"
            :alt="product.name"
            preset="productCard"
            fit="contain"
            width="400"
            height="500"
            sizes="xs:50vw sm:50vw md:33vw lg:25vw xl:25vw"
            densities="x1 x2"
            class="h-full w-full product-card-img-primary"
            :class="isUnavailable ? 'grayscale-[35%]' : ''"
            :loading="priority ? 'eager' : 'lazy'"
            :fetchpriority="priority === 'high' ? 'high' : undefined"
          />
          <AppImage
            v-if="hoverImageUrl"
            :src="hoverImageUrl"
            alt=""
            preset="productCard"
            fit="contain"
            width="400"
            height="500"
            sizes="xs:50vw sm:50vw md:33vw lg:25vw xl:25vw"
            densities="x1 x2"
            class="product-card-img-hover"
            loading="lazy"
            aria-hidden="true"
          />
        </div>
      </NuxtLink>

      <div
        v-if="isUnavailable"
        class="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-default/25"
      >
        <span class="rounded-full bg-gray-900/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
          Indisponibil
        </span>
      </div>

      <div
        v-if="hasDiscount && !isUnavailable"
        class="absolute left-3 top-3 rounded-md bg-rose-600 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm"
      >
        Reducere -{{ discountPercent }}%
      </div>

      <div
        v-if="!isUnavailable"
        class="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      >
        <ClientOnly>
          <NuxtLink
            v-if="isAdmin"
            :to="shopPath(`/admin/produse/${product.id}`)"
            class="flex h-8 w-8 items-center justify-center rounded-full bg-default/95 text-muted shadow-sm ring-1 ring-black/5 backdrop-blur-sm transition-colors hover:text-primary-600 opacity-100"
            aria-label="Editează produs"
            @click.stop
          >
            <UIcon name="i-heroicons-pencil" class="h-4 w-4" />
          </NuxtLink>
        </ClientOnly>
        <button
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-full bg-default/95 text-muted shadow-sm ring-1 ring-black/5 backdrop-blur-sm transition-colors hover:text-red-500"
          :class="{ '!text-red-500': inWishlist }"
          aria-label="Adaugă la favorite"
          @click.prevent="toggleWishlist(product)"
        >
          <UIcon :name="inWishlist ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'" class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-full bg-default/95 text-muted shadow-sm ring-1 ring-black/5 backdrop-blur-sm transition-colors hover:text-highlighted"
          :class="{ '!text-highlighted': inCompare }"
          aria-label="Compară"
          @click.prevent="cartStore.toggleCompare(product.id)"
        >
          <UIcon :name="inCompare ? 'i-heroicons-scale-solid' : 'i-heroicons-scale'" class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-full bg-default/95 text-muted shadow-sm ring-1 ring-black/5 backdrop-blur-sm transition-colors hover:text-primary-600"
          aria-label="Vizualizare rapidă"
          @click.prevent="openQuickView(product)"
        >
          <UIcon name="i-heroicons-eye" class="h-4 w-4" />
        </button>
      </div>
      <ClientOnly>
        <div
          v-if="isAdmin && isUnavailable"
          class="absolute right-2 top-2 flex gap-1"
        >
          <NuxtLink
            :to="shopPath(`/admin/produse/${product.id}`)"
            class="flex h-8 w-8 items-center justify-center rounded-full bg-default/95 text-muted shadow-sm ring-1 ring-black/5 backdrop-blur-sm transition-colors hover:text-primary-600"
            aria-label="Editează produs"
            @click.stop
          >
            <UIcon name="i-heroicons-pencil" class="h-4 w-4" />
          </NuxtLink>
        </div>
      </ClientOnly>
    </div>

    <div class="flex flex-1 flex-col">
      <NuxtLink :to="shopPath(`/produse/${product.slug}`)" class="block" @mouseenter="prefetchProduct" @focus="prefetchProduct">
        <h2
          class="text-[15px] font-bold leading-snug transition-colors"
          :class="isUnavailable ? 'text-muted' : 'text-highlighted group-hover:text-toned'"
        >
          {{ product.name }}
        </h2>
        <ul v-if="cardAttributes.length" class="mt-2 space-y-1">
          <li
            v-for="item in cardAttributes"
            :key="item.name"
            class="text-xs leading-snug"
          >
            <span class="text-muted">{{ item.name }}:</span>
            <span class="ml-1 font-medium text-toned">{{ item.value }}</span>
          </li>
        </ul>
      </NuxtLink>

      <ul class="mt-3 space-y-1.5">
        <!-- <li v-if="product.stock > 0" class="flex items-start gap-2 text-xs text-gray-500">
          <UIcon name="i-heroicons-truck" class="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
          <span>Livrare rapidă în 24–48h</span>
        </li> -->
        <li v-if="savingsAmount > 0 && !isUnavailable" class="flex items-start gap-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          <UIcon name="i-heroicons-receipt-percent" class="mt-0.5 h-4 w-4 shrink-0" />
          <span>Economisești {{ formatSavingsAmount(savingsAmount) }} {{ shopCurrencyShortLabel }}</span>
        </li>
        <li v-if="isUnavailable" class="flex items-start gap-2 text-xs text-red-500 dark:text-red-400">
          <UIcon name="i-heroicons-x-circle" class="mt-0.5 h-4 w-4 shrink-0" />
          <span>Stoc epuizat</span>
        </li>
      </ul>

      <div class="mt-auto pt-4">
        <div class="mb-3">
          <div
            v-if="hasDiscount && !isUnavailable"
            class="mb-1 flex items-start gap-0.5 text-dimmed line-through decoration-dimmed/80"
          >
            <span class="text-sm font-semibold leading-none tracking-tight">{{ originalFlipPrice.whole }}</span>
            <sup class="text-[10px] font-semibold leading-none">{{ originalFlipPrice.fraction }}</sup>
            <span class="ml-0.5 text-xs font-semibold leading-none">{{ originalFlipPrice.currency }}</span>
            <span v-if="priceUnit" class="ml-0.5 text-xs font-semibold leading-none">/ {{ priceUnit }}</span>
          </div>

          <div
            class="flex items-start gap-0.5"
            :class="isUnavailable
              ? 'text-dimmed'
              : hasDiscount
                ? 'text-rose-600 dark:text-rose-400'
                : 'text-highlighted'"
          >
            <span class="text-2xl font-bold leading-none tracking-tight">{{ flipPrice.whole }}</span>
            <sup class="text-sm font-bold leading-none">{{ flipPrice.fraction }}</sup>
            <span class="ml-1 text-lg font-bold leading-none">{{ flipPrice.currency }}</span>
            <span v-if="priceUnit" class="ml-1 text-base font-semibold leading-none">/ {{ priceUnit }}</span>
          </div>
        </div>

          <UButton
            block
            variant="solid"
            data-cy="add-to-cart"
            :color="isUnavailable ? 'neutral' : justAdded ? 'success' : 'primary'"
          :icon="justAdded
            ? 'i-heroicons-check-circle'
            : needsConfiguration
              ? 'i-heroicons-adjustments-horizontal'
              : 'i-heroicons-shopping-cart'"
          :label="isUnavailable
            ? 'Indisponibil'
            : justAdded
              ? 'Adăugat în coș'
              : configureCtaLabel
                || (hasDiscount ? 'Profită de ofertă' : 'Adaugă în coș')"
          :disabled="isUnavailable"
          :class="{ 'added-pop': justAdded }"
          @click="handleAdd"
        />
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import type { Product } from '~/types'
import {
  formatFlipPrice,
  formatSavingsAmount,
  getProductCardAttributes,
  getProductCardPricing,
} from '~/utils/productCard'
import { productIsComplex, productIsPackage } from '~/utils/productPackage'
import { productIsVariable } from '~/utils/productVariants'
import { productHasPriceUnit } from '~/utils/shopServices'

const props = withDefaults(defineProps<{
  product: Product
  /** First visible cards: eager load; pass "high" for the LCP candidate. */
  priority?: false | 'eager' | 'high'
  revealDelay?: number | false
}>(), {
  priority: false,
  revealDelay: false,
})

const shopPath = useShopPath()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const { addToCart } = useCart()
const { isAdmin } = useAuth()
const { open: openQuickView } = useQuickView()
const { prefetchRoute } = useRoutePrefetch()
const { stockManagementEnabled, shopCurrencyShortLabel, shopServicesEnabled, storefrontCardHoverImage } = useSiteSettings()

const imageWrapRef = ref<HTMLElement | null>(null)

const justAdded = ref(false)
let addedTimer: ReturnType<typeof setTimeout> | null = null

const galleryUrls = computed(() =>
  (props.product.images || [])
    .filter((img) => !img.is360 && Boolean(img.url))
    .map((img) => img.url),
)

const randomCardEnabled = computed(() => {
  const flag = props.product.randomCardImage as unknown
  return flag === true || flag === 1 || flag === '1' || flag === 'true'
})

const primaryImageUrl = computed(() =>
  props.product.images?.find((i) => i.isPrimary && !i.is360)?.url
  || galleryUrls.value[0]
  || props.product.images?.[0]?.url,
)

const cardImageUrl = ref(primaryImageUrl.value)

const hoverImageUrl = computed(() => {
  if (!storefrontCardHoverImage.value || isUnavailable.value) return null
  return galleryUrls.value.find((url) => url !== cardImageUrl.value) || null
})

function pickRandomCardImage() {
  if (!randomCardEnabled.value) {
    cardImageUrl.value = primaryImageUrl.value
    return
  }
  const urls = galleryUrls.value
  if (urls.length <= 1) {
    cardImageUrl.value = urls[0] || primaryImageUrl.value
    return
  }
  // Prefer a non-primary image when possible so the change is visible
  const primary = primaryImageUrl.value
  const others = urls.filter((url) => url !== primary)
  const pool = others.length ? others : urls
  cardImageUrl.value = pool[Math.floor(Math.random() * pool.length)]!
}

onMounted(() => {
  pickRandomCardImage()
})

watch(
  () => [props.product.id, randomCardEnabled.value, galleryUrls.value.join('|')] as const,
  () => {
    pickRandomCardImage()
  },
)

async function handleAdd() {
  if (isUnavailable.value) return
  if (needsConfiguration.value) {
    await navigateTo(shopPath(`/produse/${props.product.slug}`))
    return
  }
  await addToCart(props.product, 1, undefined, { flyFrom: imageWrapRef.value })
  justAdded.value = false
  await nextTick()
  justAdded.value = true
  if (addedTimer) clearTimeout(addedTimer)
  addedTimer = setTimeout(() => {
    justAdded.value = false
  }, 1600)
}

function prefetchProduct() {
  prefetchRoute(shopPath(`/produse/${props.product.slug}`))
}

onBeforeUnmount(() => {
  if (addedTimer) clearTimeout(addedTimer)
})

const isUnavailable = computed(() =>
  !productIsPackage(props.product)
  && stockManagementEnabled.value
  && !shopServicesEnabled.value
  && props.product.stock <= 0,
)

const isPackageProduct = computed(() => productIsPackage(props.product))
const isComplexProduct = computed(() => productIsComplex(props.product))
const isVariableProduct = computed(() => productIsVariable(props.product))

/** Complex / variable / package / measured services need choices on the product page before cart. */
const needsConfiguration = computed(() =>
  isPackageProduct.value
  || isComplexProduct.value
  || isVariableProduct.value
  || productHasPriceUnit(props.product),
)

const configureCtaLabel = computed(() => {
  if (!needsConfiguration.value) return null
  if (isPackageProduct.value) return 'Configurează pachetul'
  if (isVariableProduct.value) return 'Alege opțiunile'
  if (productHasPriceUnit(props.product)) return 'Alege cantitatea'
  return 'Configurează'
})

const priceUnit = computed(() => props.product.priceUnit?.trim() || '')

const pricing = computed(() => getProductCardPricing(props.product))
const hasDiscount = computed(() => pricing.value.hasDiscount)
const discountPercent = computed(() => pricing.value.discountPercent)
const savingsAmount = computed(() => pricing.value.savingsAmount)
const flipPrice = computed(() => formatFlipPrice(pricing.value.displayPrice))
const originalFlipPrice = computed(() => formatFlipPrice(pricing.value.originalPrice))
const cardAttributes = computed(() => getProductCardAttributes(props.product))

const inWishlist = computed(() => wishlistStore.isInWishlist(props.product.id))
const inCompare = computed(() => cartStore.isInCompare(props.product.id))

function toggleWishlist(product: Product) {
  wishlistStore.toggleWishlist(product)
}
</script>
